/* eslint-disable no-alert */
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import DatePickerField from "components/lib/Shared/DatePickerField";
import { Formik, Form } from "formik";
import { format, parseISO, isValid } from "date-fns";
import { useToast } from "hooks/useToast";
import { submitAuthorizationsAsync } from "state/slices/shareCapitalAuthorization";

const DateOfAuthorization = ({ onDateChange, isInManualUpdate }) => {
  const dispatch = useDispatch();
  const { toast } = useToast(5000);

  const shareAuthorizationSlice = useSelector(
    (state) => state.shareAuthorizationSlice
  );
  const companyId = useSelector(
    (state) => state.companyDashboardSlice?.company?._id
  );

  const authorizations = useSelector((state) =>
    (state.shareAuthorizationSlice?.shares || []).filter(
      (auth) => auth.isApproved === false
    )
  );

  const authorization = authorizations.length > 0 ? authorizations[0] : null;

  const parseDate = (input) => {
    if (!input) return null;
    if (input instanceof Date) return isValid(input) ? input : null;
    const i = parseISO(input);
    if (isValid(i)) return i;
    const d = new Date(input);
    return isValid(d) ? d : null;
  };

  // derive initial values from store
  const startDateInit = parseDate(authorization?.authorizationDate);
  const endDateInit = parseDate(authorization?.expirationDate);

  // local debounce timer
  const saveTimer = useRef(null);

  // autosave helper
  const autoSave = async (startDate, endDate) => {
    if (!companyId) return;

    // both dates must be valid to save
    if (!(startDate && endDate && isValid(startDate) && isValid(endDate)))
      return;

    const dateFields = {
      dateOfAuthorization: format(startDate, "yyyy/MM/dd"),
      dateOfExpiry: format(endDate, "yyyy/MM/dd"),
    };

    // emit to parent (keeps NominalShareCapitalTab’s state in sync)
    onDateChange?.(dateFields);

    const payload = {
      companyId,
      dateFields,
      updateDates: true,
    };

    const res = await dispatch(submitAuthorizationsAsync(payload));
    if (!submitAuthorizationsAsync.fulfilled.match(res)) {
      const errMsg =
        res?.payload?.message ||
        (typeof res?.payload === "string"
          ? res.payload
          : "Failed to update authorization dates.");
      toast("error", errMsg);
    }
  };

  // When store changes, push dates up to parent once (no save spam)
  useEffect(() => {
    const dateFields = {
      dateOfAuthorization: startDateInit
        ? format(startDateInit, "yyyy/MM/dd")
        : "",
      dateOfExpiry: endDateInit ? format(endDateInit, "yyyy/MM/dd") : "",
    };
    onDateChange?.(dateFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorization?.authorizationDate, authorization?.expirationDate]);

  return (
    <div className="w-full bg-white rounded p-4 my-6">
      <Formik
        enableReinitialize
        initialValues={{
          dateOfAuthorization: startDateInit,
          dateOfExpiry: endDateInit,
        }}
        onSubmit={() => {}}
      >
        {({ values, setFieldValue }) => {
          const handleChangeIssued = (date) => {
            // keep Formik field in sync so DatePickerField is happy
            setFieldValue("dateOfAuthorization", date);
            // debounce autosave
            if (saveTimer.current) clearTimeout(saveTimer.current);
            saveTimer.current = setTimeout(() => {
              autoSave(date, values.dateOfExpiry);
            }, 500);
          };

          const handleChangeExpiry = (date) => {
            setFieldValue("dateOfExpiry", date);
            if (saveTimer.current) clearTimeout(saveTimer.current);
            saveTimer.current = setTimeout(() => {
              autoSave(values.dateOfAuthorization, date);
            }, 500);
          };

          return (
            <Form>
              <div className="w-full p-3">
                <h1 className="my-1 text-md leading-6 font-medium text-gray-900">
                  Date of Authorization
                </h1>

                <div className="flex justify-between gap-6">
                  <div className="w-full">
                    <DatePickerField
                      label="Date Of Authorization"
                      name="dateOfAuthorization" // REQUIRED for DatePickerField (Formik context)
                      selected={values.dateOfAuthorization}
                      onChange={handleChangeIssued}
                      required
                      placeholderText="Select date of authorization"
                      errorPath=""
                      disabled={false} // always editable
                    />
                  </div>
                  <div className="w-full">
                    <DatePickerField
                      label="Date of Expiry"
                      name="dateOfExpiry" // REQUIRED for DatePickerField (Formik context)
                      selected={values.dateOfExpiry}
                      onChange={handleChangeExpiry}
                      required
                      placeholderText="Select date of expiry"
                      errorPath=""
                      disabled={false}
                    />
                  </div>
                </div>
                {/* No buttons / pencil icon — autosaves on change */}
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default DateOfAuthorization;
